import { useMemo, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  ArrowRight,
  BarChart3,
  Beaker,
  CheckCircle2,
  ClipboardList,
  LineChart,
  ShieldCheck,
  Users,
} from "lucide-react";
import { SiteFooter, SiteNav } from "@/components/site-chrome";
import { AuditForm, type AuditFormValues } from "@/components/audit-form";
import { PaymentForm } from "@/components/payment-form";
import { Faq } from "@/components/faq";
import { Testimonials } from "@/components/testimonials";
import {
  EnrichedFindingsAccordion,
  type EnrichedFinding,
} from "@/components/enriched-findings-accordion";
import { TrackingAuditDisplay, type TrackingAuditData } from "@/components/tracking-audit-display";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function App() {
  return <Index />;
}

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "https://audit-platform-backend-git-main-dave-cro-audits-tg.vercel.app";
const STRIPE_PK = (import.meta.env.VITE_STRIPE_PUBLIC_KEY as string | undefined) ?? "";

type Step = "landing" | "results" | "payment" | "success";
type AuditFinding = EnrichedFinding;

type Vertical =
  | "ECOMMERCE_DTC"
  | "LUXURY_ECOMMERCE"
  | "SAAS_B2B"
  | "B2B_MEDICAL"
  | "CONTENT_PUBLISHING"
  | "SAAS_CONSUMER"
  | "ENTERPRISE_SAAS"
  | "MARKETPLACES"
  | "CREATOR_ECONOMY"
  | "DEVTOOLS"
  | "UNKNOWN";

function getVerticalLabel(vertical: Vertical | string): string {
  const labels: Record<string, string> = {
    ECOMMERCE_DTC: "E-Commerce",
    LUXURY_ECOMMERCE: "Luxury E-Commerce",
    SAAS_B2B: "SaaS B2B",
    B2B_MEDICAL: "Healthcare",
    CONTENT_PUBLISHING: "Content Publishing",
    SAAS_CONSUMER: "SaaS Consumer",
    ENTERPRISE_SAAS: "Enterprise SaaS",
    MARKETPLACES: "Marketplaces",
    CREATOR_ECONOMY: "Creator Economy",
    DEVTOOLS: "Developer Tools",
    UNKNOWN: "General",
  };
  return labels[vertical as string] || "General";
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function Index() {
  const [step, setStep] = useState<Step>("landing");
  const [formData, setFormData] = useState<AuditFormValues>({
    website_url: "",
    email: "",
    accepted_legal: false,
    marketing_opt_in: false,
  });
  const [auditId, setAuditId] = useState<string>("");
  const [submittedAt, setSubmittedAt] = useState<string>("");
  const [findings, setFindings] = useState<AuditFinding[] | null>(null);
  const [trackingAudit, setTrackingAudit] = useState<TrackingAuditData | null>(null);
  const [vertical, setVertical] = useState<Vertical>("UNKNOWN");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (STRIPE_PK ? loadStripe(STRIPE_PK) : null),
    [],
  );

  const handleSubmit = async (values: AuditFormValues) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/submit-audit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        setError(data.error ?? "We couldn't submit your audit. Please try again.");
        setIsLoading(false);
        return;
      }
      setFormData(values);
      setAuditId(data.audit_id ?? "");
      setSubmittedAt(new Date().toLocaleString());
      setVertical((data.vertical as Vertical) ?? "UNKNOWN");
      setFindings(Array.isArray(data.findings) ? data.findings : null);
      setTrackingAudit(data.tracking_audit ?? null);
      setStep("results");
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
      if (!Array.isArray(data.findings)) {
        setTimeout(() => {
          setFindings([
            {
              id: "fallback-001",
              title: "Above-the-fold value prop is unclear",
              description: "Visitors can't tell what the offer is in 5 seconds — costing first-scroll engagement.",
              detailedInsight: "Your value proposition isn't immediately visible. Users form opinions in milliseconds.",
              recommendedFix: "Lead with the primary benefit in the headline and hero image.",
              impact: "high",
              category: "Content",
              pillar: "Conversion",
              researchCitations: [
                {
                  id: "conv-fallback-001",
                  inline: "Users decide within 5 seconds if a page is relevant to them.",
                  whyItMatters: "First impressions drive engagement. A clear value prop in the hero section increases time-on-page and conversion.",
                  source: "Nielsen Norman Group",
                  url: "https://www.nngroup.com/articles/how-long-do-users-stay-on-web-pages/"
                }
              ],
              observedMetric: {
                label: "Above-fold clarity",
                value: "Unclear",
                unit: "assessment"
              }
            },
            {
              id: "fallback-002",
              title: "Primary CTA lacks contrast and specificity",
              description: "The CTA blends with surrounding UI; copy is generic ('Learn more') instead of outcome-led.",
              detailedInsight: "Vague CTAs reduce conversion. Users need to know what happens when they click.",
              recommendedFix: [
                "Use outcome-led copy: 'See pricing' instead of 'Learn more'",
                "Increase visual contrast against the background",
                "Make button larger and more prominent"
              ],
              impact: "high",
              category: "UI/UX",
              pillar: "Conversion",
              researchCitations: [
                {
                  id: "conv-fallback-002",
                  inline: "Specific CTAs outperform generic ones by 40%+ in A/B tests.",
                  whyItMatters: "Users respond to clear, benefit-driven language. Generic CTAs create friction and reduce conversion rates.",
                  source: "Baymard Institute",
                  url: "https://baymard.com/blog/call-to-action-buttons"
                }
              ],
              observedMetric: {
                label: "CTA copy type",
                value: "Generic",
                unit: "assessment"
              }
            },
            {
              id: "fallback-003",
              title: "Trust signals load below the fold",
              description: "Social proof and credibility markers aren't visible until ~70% scroll depth.",
              detailedInsight: "Trust is built early. Users need proof points visible immediately, not after scrolling.",
              recommendedFix: [
                "Move customer reviews or ratings to the hero section",
                "Add logos of recognizable customers or media mentions above the fold",
                "Include security badges or certifications near the primary CTA"
              ],
              impact: "medium",
              category: "Trust",
              pillar: "Trust",
              researchCitations: [
                {
                  id: "trust-fallback-001",
                  inline: "Trust signals above the fold increase conversion by 15-25%.",
                  whyItMatters: "Users evaluate credibility within the first few seconds. Early visibility of social proof, testimonials, or certifications reduces friction and increases purchase confidence.",
                  source: "Nielsen Norman Group",
                  url: "https://www.nngroup.com/articles/social-proof-ux/"
                }
              ],
              observedMetric: {
                label: "Trust signals visibility",
                value: "Below fold",
                unit: "location"
              }
            },
          ]);
        }, 1400);
      }
    } catch {
      setError("We couldn't reach our servers. Please try again in a moment.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    setIsPaying(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/create-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ audit_id: auditId, email: formData.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.client_secret) {
        setError(data.error ?? "We couldn't start checkout. Please try again.");
        setIsPaying(false);
        return;
      }
      setClientSecret(data.client_secret);
      setStep("payment");
    } catch {
      setError("We couldn't reach our servers. Please try again in a moment.");
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))] text-[color:hsl(var(--foreground))]">
      <SiteNav />

      <main id="audit" className="pt-20 md:pt-24">
        {step === "landing" && (
          <LandingView
            onSubmit={handleSubmit}
            isLoading={isLoading}
            serverError={error}
          />
        )}

        {step === "results" && (
          <ResultsView
            domain={getDomain(formData.website_url)}
            email={formData.email}
            auditId={auditId}
            submittedAt={submittedAt}
            vertical={vertical}
            findings={findings}
            trackingAudit={trackingAudit}
            onPay={handlePayment}
            isLoading={isPaying}
            serverError={error}
          />
        )}

        {step === "payment" && clientSecret && stripePromise && (
          <section className="section-pad">
            <div className="container-tg">
              <div className="mx-auto max-w-[720px] rounded-2xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface))] p-8 fade-in">
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: "night",
                      variables: {
                        colorPrimary: "#34E5A6",
                        colorText: "#FAFAFA",
                        colorBackground: "#1E1E1E",
                        fontFamily: "Inter, system-ui, sans-serif",
                        borderRadius: "10px",
                      },
                    },
                  }}
                >
                  <PaymentForm
                    domain={getDomain(formData.website_url)}
                    email={formData.email}
                    auditId={auditId}
                    apiUrl={API_URL}
                    onSuccess={() => setStep("success")}
                  />
                </Elements>
              </div>
            </div>
          </section>
        )}

        {step === "payment" && (!clientSecret || !stripePromise) && (
          <section className="section-pad">
            <div className="container-tg">
              <div className="mx-auto max-w-[720px] rounded-2xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface))] p-8 text-center">
                <p className="text-[15px] text-[color:hsl(var(--destructive))]">
                  Payment is unavailable. Please configure VITE_STRIPE_PUBLIC_KEY.
                </p>
              </div>
            </div>
          </section>
        )}

        {step === "success" && <SuccessView email={formData.email} />}
      </main>

      <SiteFooter />
    </div>
  );
}

/* ──────────── LANDING ──────────── */

function LandingView({
  onSubmit,
  isLoading,
  serverError,
}: {
  onSubmit: (v: AuditFormValues) => Promise<void>;
  isLoading: boolean;
  serverError: string | null;
}) {
  return (
    <>
      {/* Hero */}
      <section className="bg-mesh">
        <div className="container-tg pt-12 md:pt-20 pb-20 md:pb-28">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <p className="label-eyebrow">Free mini-audit · No credit card</p>
              <h1 className="heading-display mt-5 text-white text-balance">
                Get your website{" "}
                <span className="brand-grad-text">audited.</span>
              </h1>
              <p className="mt-6 max-w-[560px] text-lg md:text-xl text-[color:hsl(var(--muted-foreground))]">
                Drop in your URL and we'll send back a free mini-audit of the friction
                costing you conversions. Unlock the full evidence-based audit for{" "}
                <span className="text-white tabular">$197</span>.
              </p>

              <div className="mt-12 hidden lg:block">
                <HeroChart />
              </div>
            </div>

            <div className="flex justify-start lg:justify-end">
              <AuditForm onSubmit={onSubmit} isLoading={isLoading} serverError={serverError} />
            </div>
          </div>
        </div>
      </section>

      <hr className="section-divider" />

      {/* Trust strip */}
      <section className="border-b border-[color:hsl(var(--border))]">
        <div className="container-tg grid gap-8 py-10 sm:grid-cols-3">
          <TrustItem Icon={LineChart} text="Backed by 8-week test cycles" />
          <TrustItem Icon={Users} text="Used by ecommerce + SaaS teams" />
          <TrustItem Icon={ShieldCheck} text="Methodology trusted by senior CRO leads" />
        </div>
        {/* TODO: real client logos go here */}
      </section>

      {/* What you'll get */}
      <section className="section-pad">
        <div className="container-tg">
          <div className="max-w-2xl">
            <p className="label-eyebrow mb-4">The full audit</p>
            <h2 className="heading-h2 text-white">
              What's in the <span className="brand-grad-text">full audit.</span>
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <FeatureCard
              Icon={ClipboardList}
              title="Friction map"
              body="A prioritized list of the UX issues bleeding conversions, ranked by impact and effort."
            />
            <FeatureCard
              Icon={BarChart3}
              title="Evidence pack"
              body="Heuristic findings tied to behavioural data and CRO best practice — no opinions, just signal."
            />
            <FeatureCard
              Icon={Beaker}
              title="Test backlog"
              body="5–10 hypotheses ready for A/B testing, scored by ICE so you know what to ship first."
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-pad bg-[color:hsl(var(--surface)/0.5)] border-y border-[color:hsl(var(--border))]">
        <div className="container-tg">
          <div className="max-w-2xl">
            <p className="label-eyebrow mb-4">How it works</p>
            <h2 className="heading-h2 text-white">Three steps. No back-and-forth.</h2>
          </div>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            <Step n={1} title="Submit your URL" body="Drop your URL and email. Takes 10 seconds." />
            <Step n={2} title="We analyze the page" body="Automated heuristics + senior consultant review." />
            <Step n={3} title="Get your prioritized fixes" body="A clear, ranked backlog you can ship from this week." />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <section className="section-pad border-t border-[color:hsl(var(--border))]">
        <div className="container-tg">
          <div className="max-w-2xl">
            <p className="label-eyebrow mb-4">FAQ</p>
            <h2 className="heading-h2 text-white">Common questions.</h2>
          </div>
          <div className="mt-10 max-w-[820px]">
            <Faq />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-pad bg-[color:hsl(var(--surface)/0.5)] border-y border-[color:hsl(var(--border))]">
        <div className="container-tg flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <p className="heading-h3 text-white max-w-xl">
            Ready to find what's costing you conversions?
          </p>
          <a href="#audit" className="cta-primary">
            Start your free audit
            <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </>
  );
}

function TrustItem({ Icon, text }: { Icon: typeof LineChart; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <Icon
        size={20}
        strokeWidth={1.5}
        className="shrink-0 mt-0.5 text-[color:hsl(var(--primary))]"
      />
      <p className="text-[15px] text-white">{text}</p>
    </div>
  );
}

function FeatureCard({
  Icon,
  title,
  body,
}: {
  Icon: typeof LineChart;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface))] p-7">
      <Icon size={26} strokeWidth={1.5} className="text-[color:hsl(var(--primary))]" />
      <h3 className="heading-h3 mt-5 text-white">{title}</h3>
      <p className="mt-2 body-text">{body}</p>
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div>
      <div className="tabular text-[14px] font-semibold brand-grad-text">
        {String(n).padStart(2, "0")}
      </div>
      <h3 className="heading-h3 mt-3 text-white">{title}</h3>
      <p className="mt-2 body-text">{body}</p>
    </div>
  );
}

function HeroChart() {
  return (
    <svg viewBox="0 0 480 160" className="w-full max-w-[480px]" aria-hidden="true">
      <defs>
        <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#4287EB" />
          <stop offset="100%" stopColor="#34E5A6" />
        </linearGradient>
        <pattern id="grid" width="60" height="40" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="480" height="160" fill="url(#grid)" />
      <path
        d="M 0 130 C 80 125, 140 120, 200 110 S 320 95, 480 35"
        fill="none"
        stroke="url(#line-grad)"
        strokeWidth="2.5"
      />
      <path
        d="M 0 140 C 80 138, 160 135, 240 130 S 360 125, 480 118"
        fill="none"
        stroke="rgba(255,255,255,0.4)"
        strokeWidth="1.5"
        strokeDasharray="3 4"
      />
      <circle cx="480" cy="35" r="4" fill="#34E5A6" />
      <text x="370" y="28" fontSize="12" fill="#FAFAFA" fontWeight="600">
        +34% conversion
      </text>
    </svg>
  );
}

/* ──────────── RESULTS ──────────── */

function ResultsView({
  domain,
  email,
  auditId,
  submittedAt,
  vertical,
  findings,
  trackingAudit,
  onPay,
  isLoading,
  serverError,
}: {
  domain: string;
  email: string;
  auditId: string;
  submittedAt: string;
  vertical: Vertical;
  findings: AuditFinding[] | null;
  trackingAudit: TrackingAuditData | null;
  onPay: () => void;
  isLoading: boolean;
  serverError: string | null;
}) {
  return (
    <section className="section-pad">
      <div className="container-tg">
        <div className="mx-auto max-w-[720px] rounded-2xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface))] p-8 md:p-10 fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-[color:hsl(var(--success))]" />
              <span className="text-[13px] font-medium text-[color:hsl(var(--success))]">
                Mini-audit ready
              </span>
            </div>
          </div>

          <h2 className="heading-h2 mt-4 text-white">
            Here's what we found on{" "}
            <span className="brand-grad-text">{domain}</span>
          </h2>
          <p className="mt-2 text-[13px] text-[color:hsl(var(--muted-foreground))] tabular">
            Requested {submittedAt}
          </p>
          <p className="mt-3 text-[13px] text-[color:hsl(var(--foreground))]">
            <span className="font-semibold">Business Type:</span> {getVerticalLabel(vertical)}
          </p>

          <Tabs defaultValue="cro" className="mt-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="cro">CRO Audit</TabsTrigger>
              <TabsTrigger value="tracking">Tracking Audit</TabsTrigger>
            </TabsList>

            <TabsContent value="cro" className="mt-6">
              <EnrichedFindingsAccordion findings={findings} loading={!findings} />
            </TabsContent>

            <TabsContent value="tracking" className="mt-6">
              <TrackingAuditDisplay data={trackingAudit} loading={!trackingAudit} />
            </TabsContent>
          </Tabs>

          <hr className="section-divider my-10" />

          <div>
            <p className="label-eyebrow">Unlock the full audit</p>
            <h3 className="mt-3 text-2xl md:text-[28px] font-semibold text-white tracking-tight">
              Get the full evidence-based audit for{" "}
              <span className="brand-grad-text tabular">$197</span>
            </h3>

            <ul className="mt-6 space-y-3">
              {[
                "Complete friction map across 30+ heuristics",
                "Prioritized A/B test backlog with ICE scores",
                "Senior CRO consultant review before delivery",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-white">
                  <CheckCircle2
                    size={18}
                    className="mt-0.5 shrink-0 text-[color:hsl(var(--primary))]"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="cta-primary cta-primary--block mt-8"
              onClick={onPay}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? (
                "Preparing checkout…"
              ) : (
                <>
                  Pay <span className="tabular">$197</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
            <p className="mt-3 text-center text-[12px] text-[color:hsl(var(--subtle))]">
              Secure payment via Stripe · Instant delivery within 48h · Sent to {email}
            </p>

            {serverError && (
              <p className="mt-4 text-center text-[13px] text-[color:hsl(var(--destructive))]" role="alert">
                {serverError}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────── SUCCESS ──────────── */

function SuccessView({ email }: { email: string }) {
  return (
    <section className="section-pad">
      <div className="container-tg">
        <div className="mx-auto max-w-[640px] rounded-2xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--surface))] p-10 text-center fade-in">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
            style={{ background: "hsl(var(--primary) / 0.15)" }}
          >
            <CheckCircle2 size={36} className="text-[color:hsl(var(--primary))]" />
          </div>
          <h2 className="heading-h2 mt-6 text-white">Payment received. We're on it.</h2>
          <p className="mt-3 text-[16px] text-[color:hsl(var(--muted-foreground))]">
            Your full audit is being reviewed by a senior CRO consultant. You'll receive
            it at <span className="text-white">{email}</span> within 48 hours.
          </p>
          <a
            href="https://testedgrowth.com"
            className="cta-outline mt-8"
          >
            Back to Tested Growth <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
